import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const PromptConfigSchema = z.object({
  key: z.string(),
  content: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

const ListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(PromptConfigSchema),
});

export function usePromptConfigs() {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["prompt-configs"],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: "/api/v1/prompt-configs",
      });
      return ListResponseSchema.parse(json).data;
    },
  });
}

export function useSavePromptConfig() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ key, content }: { key: string; content: string }) =>
      backend({
        method: "PATCH",
        url: `/api/v1/prompt-configs/${key}`,
        data: { content },
      }),
    onSuccess: () => {
      toast.success(t("prompts.saved"));
      queryClient.invalidateQueries({ queryKey: ["prompt-configs"] });
    },
  });
}
